import type { KeyboardEvent, ReactNode } from 'react'
import type { SearchItem } from '@/lib/market/types'

import { useRouter } from 'next/router'
import { ArrowRight, CornerDownLeft, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { allNav } from '@/constants/site'

interface SearchDialogProps {
    items: SearchItem[]
}

interface Result {
    key: string
    href: string
    item: SearchItem | null
    label: string
    description: string
}

const rankItem = (item: SearchItem, query: string): number => {
    const ticker = item.ticker.toLowerCase()
    const name = item.name.toLowerCase()
    const sector = (item.sectorName ?? '').toLowerCase()

    if (ticker === query) return 0
    if (ticker.startsWith(query)) return 1
    if (name.startsWith(query)) return 2
    if (name.includes(query)) return 3
    if (sector.includes(query)) return 4

    return -1
}

const SearchDialog = ({ items }: SearchDialogProps): ReactNode => {
    const router = useRouter()
    const dialogRef = useRef<HTMLDialogElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const [query, setQuery] = useState('')
    const [active, setActive] = useState(0)

    const open = () => {
        const dialog = dialogRef.current
        if (!dialog || dialog.open) return

        dialog.showModal()
        window.setTimeout(() => inputRef.current?.focus(), 10)
    }

    const close = () => dialogRef.current?.close()

    useEffect(() => {
        const onKey = (event: globalThis.KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)

            if ((event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey)) {
                event.preventDefault()
                open()
            }
            else if (event.key === '/' && !typing) {
                event.preventDefault()
                open()
            }
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [])

    useEffect(() => {
        const handleRoute = () => close()
        router.events.on('routeChangeStart', handleRoute)

        return () => router.events.off('routeChangeStart', handleRoute)
    }, [router.events])

    const results: Result[] = useMemo(() => {
        const text = query.trim().toLowerCase()

        if (!text) {
            const pages = allNav.map(page => ({ key: `page-${page.href}`, href: page.href, item: null, label: page.label, description: page.description }))
            const shares = items.slice(0, 6).map(item => ({ key: item.id, href: `/stocks/${item.slug}`, item, label: item.name, description: item.ticker }))

            return [...shares, ...pages]
        }

        const matches = items
            .map(item => ({ item, rank: rankItem(item, text) }))
            .filter(entry => entry.rank >= 0)
            .sort((a, b) => a.rank - b.rank || a.item.ticker.localeCompare(b.item.ticker))
            .slice(0, 8)
            .map(({ item }) => ({ key: item.id, href: `/stocks/${item.slug}`, item, label: item.name, description: item.ticker }))

        const pages = allNav
            .filter(page => page.label.toLowerCase().includes(text) || page.description.toLowerCase().includes(text))
            .map(page => ({ key: `page-${page.href}`, href: page.href, item: null, label: page.label, description: page.description }))

        return [...matches, ...pages]
    }, [items, query])

    useEffect(() => {
        setActive(0)
    }, [query])

    const go = (result: Result | undefined) => {
        if (!result) return

        close()
        void router.push(result.href)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActive(current => Math.min(results.length - 1, current + 1))
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive(current => Math.max(0, current - 1))
        }
        else if (event.key === 'Enter') {
            event.preventDefault()
            go(results[active])
        }
    }

    return (
        <>
            <button
                type='button'
                onClick={open}
                className='flex h-9 items-center gap-2 rounded-lg border border-border bg-secondary/70 px-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground sm:w-56'
                aria-label='Search shares and pages'
            >
                <Search aria-hidden='true' className='size-4' />
                <span className='hidden sm:inline'>Search ticker or company</span>
                <kbd className='ml-auto hidden rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline'>/</kbd>
            </button>

            <dialog
                ref={dialogRef}
                aria-label='Search'
                onClose={() => setQuery('')}
                onClick={event => {
                    if (event.target === dialogRef.current) close()
                }}
                className='mx-auto mt-[12vh] w-[min(640px,calc(100vw-2rem))] max-w-none rounded-xl border border-border bg-popover p-0 text-foreground shadow-2xl'
            >
                <div className='flex items-center gap-3 border-b border-border px-4'>
                    <Search aria-hidden='true' className='size-4 shrink-0 text-muted-foreground' />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder='Search by ticker, company or sector'
                        aria-label='Search by ticker, company or sector'
                        aria-controls='search-results'
                        aria-activedescendant={results[active] ? `search-${results[active].key}` : undefined}
                        className='h-12 min-w-0 flex-1 bg-transparent text-[15px] text-foreground outline-none placeholder:text-muted-foreground'
                    />
                    <button type='button' onClick={close} aria-label='Close search' className='rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground'>
                        <X aria-hidden='true' className='size-4' />
                    </button>
                </div>

                <ul id='search-results' role='listbox' aria-label='Search results' className='max-h-[56vh] overflow-y-auto p-2 scroll-thin'>
                    {results.length === 0 && (
                        <li className='px-3 py-8 text-center text-sm text-muted-foreground'>
                            No shares or pages match that search.
                        </li>
                    )}

                    {results.map((result, index) => (
                        <li
                            key={result.key}
                            id={`search-${result.key}`}
                            role='option'
                            aria-selected={index === active}
                        >
                            <button
                                type='button'
                                onClick={() => go(result)}
                                onMouseEnter={() => setActive(index)}
                                className={cn(
                                    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left',
                                    index === active ? 'bg-secondary' : 'hover:bg-secondary/60'
                                )}
                            >
                                {result.item ? (
                                    <>
                                        <span className='w-14 shrink-0 font-mono text-xs font-semibold text-primary'>{result.item.ticker}</span>
                                        <span className='min-w-0 flex-1'>
                                            <span className='block truncate text-sm text-foreground'>
                                                <span data-mdlfy-cms={`companies:${result.item.id}:name`}>{result.item.name}</span>
                                            </span>
                                            {result.item.sectorName && result.item.sectorId && (
                                                <span className='block truncate text-xs text-muted-foreground'>
                                                    <span data-mdlfy-cms={`sectors:${result.item.sectorId}:name`}>{result.item.sectorName}</span>
                                                </span>
                                            )}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className='w-14 shrink-0 text-xs text-muted-foreground'>Page</span>
                                        <span className='min-w-0 flex-1'>
                                            <span className='block truncate text-sm text-foreground'>{result.label}</span>
                                            <span className='block truncate text-xs text-muted-foreground'>{result.description}</span>
                                        </span>
                                    </>
                                )}

                                {index === active
                                    ? <CornerDownLeft aria-hidden='true' className='size-4 shrink-0 text-muted-foreground' />
                                    : <ArrowRight aria-hidden='true' className='size-4 shrink-0 text-muted-foreground/50' />}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center justify-between border-t border-border px-4 py-2 text-[11px] text-muted-foreground'>
                    <span>Arrow keys to move, Enter to open</span>
                    <span>Esc to close</span>
                </div>
            </dialog>
        </>
    )
}

export default SearchDialog
